import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { Container } from "react-bootstrap";
import { loadLabProgress, saveLabProgress, subscribeLabHealth } from "../../lib/firebase";
import { useAuth, hasRole } from "../../context/AuthContext";
import SharedAuthGate from "../../components/AuthGate";
import Instructions from "../../components/Instructions/Instructions";
import { verifyLab } from "../../lib/functions";
import LivesIndicator from "../gamification/LivesIndicator";
import LabTerminal from "../lab/LabTerminal";

// ── Styled Components (Nothing Design) ────────────────────────────

const Section = styled.section`
  padding: 96px 0 48px;
  background: var(--nd-black);
  min-height: 100vh;
`;

const BackLink = styled.button`
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--nd-text-disabled);
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  margin-bottom: 32px;
  display: block;
  outline: none;
  &:hover { color: var(--nd-text-primary); }
`;

const Badges = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

const Badge = styled.span`
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  padding: 4px 12px;
  border-radius: 4px;
  background: ${({ variant }) =>
    variant === "easy" ? "rgba(74,158,92,0.15)" :
    variant === "medium" ? "rgba(212,168,67,0.15)" :
    variant === "hard" ? "rgba(215,25,33,0.15)" :
    "rgba(255,255,255,0.05)"};
  color: ${({ variant }) =>
    variant === "easy" ? "var(--nd-success)" :
    variant === "medium" ? "var(--nd-warning)" :
    variant === "hard" ? "var(--nd-accent)" :
    "var(--nd-text-disabled)"};
`;

const Title = styled.h1`
  font-family: 'Doto', 'Space Mono', monospace;
  font-size: 48px;
  font-weight: 700;
  color: var(--nd-text-display);
  letter-spacing: -0.03em;
  line-height: 1.0;
  margin-bottom: 16px;
  @media (max-width: 768px) { font-size: 32px; }
`;

const Desc = styled.p`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 18px;
  color: var(--nd-text-secondary);
  line-height: 1.6;
  max-width: 600px;
  margin-bottom: 48px;
`;

const Card = styled.div`
  background: var(--nd-surface);
  border: 1px solid var(--nd-border);
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 24px;
`;

const CardLabel = styled.h3`
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--nd-text-disabled);
  margin-bottom: 16px;
`;

const PrereqItem = styled.li`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 14px;
  color: var(--nd-text-secondary);
  padding: 4px 0;
  list-style: none;
  &::before { content: '· '; color: var(--nd-text-disabled); }
`;

const ObjectiveRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid var(--nd-border);
  &:last-child { border-bottom: none; }
`;

const Checkbox = styled.button`
  width: 22px;
  height: 22px;
  min-width: 22px;
  border-radius: 4px;
  border: 1px solid ${({ checked }) => checked ? "var(--nd-success)" : "var(--nd-border-visible)"};
  background: ${({ checked }) => checked ? "rgba(74,158,92,0.15)" : "transparent"};
  color: var(--nd-success);
  font-size: 12px;
  cursor: pointer;
  outline: none;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 2px;
`;

const ObjectiveText = styled.span`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 14px;
  color: ${({ checked }) => checked ? "var(--nd-text-disabled)" : "var(--nd-text-primary)"};
  text-decoration: ${({ checked }) => checked ? "line-through" : "none"};
  line-height: 1.5;
`;

const HintHeader = styled.button`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 14px;
  color: var(--nd-text-primary);
  background: none;
  border: none;
  padding: 12px 0;
  width: 100%;
  text-align: left;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  outline: none;
  border-bottom: 1px solid var(--nd-border);
  &:hover { color: var(--nd-text-display); }
`;

const HintArrow = styled.span`
  font-size: 12px;
  color: var(--nd-text-disabled);
  transition: transform 200ms ease;
  transform: ${({ open }) => open ? "rotate(90deg)" : "rotate(0)"};
`;

const HintBody = styled.div`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 14px;
  color: var(--nd-text-secondary);
  line-height: 1.6;
  padding: 12px 0;
  display: ${({ open }) => open ? "block" : "none"};
`;

const ResourceLink = styled.a`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 14px;
  color: var(--nd-interactive) !important;
  text-decoration: none;
  display: block;
  padding: 8px 0;
  border-bottom: 1px solid var(--nd-border);
  &:last-child { border-bottom: none; }
  &:hover { text-decoration: underline; }
`;

const ActionRow = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 32px;
  flex-wrap: wrap;
`;

const PrimaryBtn = styled.button`
  font-family: 'Space Mono', monospace;
  font-size: 13px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  padding: 12px 24px;
  min-height: 44px;
  border-radius: 999px;
  border: none;
  background: var(--nd-text-display);
  color: var(--nd-black);
  cursor: pointer;
  outline: none;
  transition: opacity 250ms ease;
  &:hover { opacity: 0.85; }
  &:disabled { opacity: 0.3; cursor: default; }
`;

const SecondaryBtn = styled.a`
  font-family: 'Space Mono', monospace;
  font-size: 13px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  padding: 12px 24px;
  min-height: 44px;
  border-radius: 999px;
  border: 1px solid var(--nd-border-visible);
  background: transparent;
  color: var(--nd-text-primary) !important;
  cursor: pointer;
  text-decoration: none !important;
  display: inline-flex;
  align-items: center;
  transition: border-color 250ms ease;
  &:hover { border-color: var(--nd-text-primary); }
`;

const SignInPanel = styled.div`
  text-align: center;
  padding: 96px 0;
`;

const AuthTitle = styled.h2`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 24px;
  color: var(--nd-text-display);
  margin-bottom: 12px;
`;

const AuthDesc = styled.p`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 16px;
  color: var(--nd-text-secondary);
  margin-bottom: 32px;
`;

const CompleteBadge = styled.div`
  font-family: 'Space Mono', monospace;
  font-size: 13px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--nd-success);
  padding: 8px 20px;
  border: 1px solid var(--nd-success);
  border-radius: 999px;
  display: inline-block;
`;

const ProgressLabel = styled.div`
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--nd-text-disabled);
  margin-top: 16px;
`;

// ── Component ──────────────────────────────────────────────────────

const SaveError = styled.div`
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--nd-accent);
  margin-top: 8px;
`;

const HealthBanner = styled.div`
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--nd-accent);
  border: 1px solid var(--nd-accent);
  border-radius: 6px;
  padding: 10px 14px;
  margin-bottom: 16px;
`;

const labHealthBannerText = (h) => {
  if (!h || h.ok !== false) return null;
  const parts = [];
  if (!h.k8s) parts.push("Kubernetes");
  if (!h.vm) parts.push("Docker VM");
  const what = parts.length ? parts.join(" + ") : (h.reason || "infrastructure");
  return `Live labs are temporarily unavailable — ${what} is down. Try again shortly.`;
};

const LabDetail = ({ lab, onBack }) => {
  const { user, role, signIn, loading: authLoading } = useAuth();
  const [_progressRef, setProgressRef] = useState(null);
  const [checks, setChecks] = useState([]);
  const [revealedHints, setRevealedHints] = useState([]);
  const [completed, setCompleted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saveError, setSaveError] = useState("");
  const [shellOpen, setShellOpen] = useState(false);
  const [labHealth, setLabHealth] = useState(null);

  useEffect(() => {
    return subscribeLabHealth(setLabHealth);
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const data = await loadLabProgress(user.uid);
        if (cancelled) return;
        if (data) {
          setProgressRef(data);
          const savedChecks = data.objectiveChecks?.[lab.id] || [];
          const normalized = lab.objectives.map((_, i) => savedChecks[i] || false);
          setChecks(normalized);
          setRevealedHints(data.hintsRevealed?.[lab.id] || []);
          setCompleted(data.completedLabs?.includes(lab.id) || false);
        } else {
          setChecks(new Array(lab.objectives.length).fill(false));
        }
      } catch (err) {
        console.error("Failed to load progress:", err);
        if (!cancelled) setChecks(new Array(lab.objectives.length).fill(false));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user, authLoading, lab.id, lab.objectives.length]);

  // Use functional updates to avoid stale state race condition
  const persistProgress = async (getUpdated) => {
    if (!user) return;
    setSaveError("");
    try {
      setProgressRef(prev => {
        const updated = getUpdated(prev);
        // Fire and forget with error handling
        saveLabProgress(user.uid, updated).catch(err => {
          console.error("Failed to save:", err);
          setSaveError("SAVE FAILED — CHECK CONNECTION");
        });
        return updated;
      });
    } catch (err) {
      setSaveError("SAVE FAILED");
    }
  };

  const toggleCheck = (idx) => {
    setChecks(prev => {
      const newChecks = [...prev];
      newChecks[idx] = !newChecks[idx];
      // If unchecking and was completed, undo completion
      const allChecked = newChecks.every(Boolean);
      if (completed && !allChecked) {
        setCompleted(false);
      }
      persistProgress(prevProgress => ({
        ...prevProgress,
        objectiveChecks: { ...(prevProgress?.objectiveChecks || {}), [lab.id]: newChecks },
        completedLabs: (completed && !allChecked)
          ? (prevProgress?.completedLabs || []).filter(id => id !== lab.id)
          : (prevProgress?.completedLabs || []),
        startedAt: { ...(prevProgress?.startedAt || {}), [lab.id]: prevProgress?.startedAt?.[lab.id] || new Date().toISOString() },
      }));
      return newChecks;
    });
  };

  const toggleHint = (idx) => {
    setRevealedHints(prev => {
      if (prev.includes(idx)) return prev;
      const newHints = [...prev, idx];
      persistProgress(prevProgress => ({
        ...prevProgress,
        hintsRevealed: { ...(prevProgress?.hintsRevealed || {}), [lab.id]: newHints },
      }));
      return newHints;
    });
  };

  const markComplete = () => {
    setCompleted(true);
    persistProgress(prevProgress => ({
      ...prevProgress,
      completedLabs: [...new Set([...(prevProgress?.completedLabs || []), lab.id])],
      completedAt: { ...(prevProgress?.completedAt || {}), [lab.id]: new Date().toISOString() },
    }));
    // Parallel gamification path — must never block the existing UX.
    verifyLab({ labId: lab.id, passed: true, objectives: checks })
      .then((res) => {
        if (res && !res.ok && res.reason === "out_of_lives") {
          setSaveError("OUT OF LIVES — TRY AGAIN AFTER REGEN");
        }
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.warn("[gamification] verifyLab failed", err && err.message);
      });
  };

  const undoComplete = () => {
    setCompleted(false);
    persistProgress(prevProgress => ({
      ...prevProgress,
      completedLabs: (prevProgress?.completedLabs || []).filter(id => id !== lab.id),
    }));
  };

  if (loading) {
    return (
      <Section>
        <Container>
          <BackLink onClick={onBack}>← BACK TO LABS</BackLink>
          <ProgressLabel>LOADING...</ProgressLabel>
        </Container>
      </Section>
    );
  }

  // Auth gate
  if (!user) {
    return (
      <Section>
        <Container>
          <BackLink onClick={onBack}>← BACK TO LABS</BackLink>
          <Badges>
            <Badge variant={lab.difficulty}>{lab.difficulty}</Badge>
            <Badge variant="default">{lab.category}</Badge>
            <Badge variant="default">{lab.time}</Badge>
          </Badges>
          <Title>{lab.title}</Title>
          <Desc>{lab.description}</Desc>

          <SignInPanel>
            <AuthTitle>Sign in to start this lab</AuthTitle>
            <AuthDesc>
              Your progress is saved automatically. Pick up where you left off.
            </AuthDesc>
            <PrimaryBtn onClick={signIn} aria-label="Sign in with Google">
              SIGN IN WITH GOOGLE
            </PrimaryBtn>
          </SignInPanel>
        </Container>
      </Section>
    );
  }

  // Tier gate — advanced labs require client+
  if (lab.tier === "advanced" && !hasRole(role, "client")) {
    return (
      <Section>
        <Container>
          <BackLink onClick={onBack}>← BACK TO LABS</BackLink>
          <SharedAuthGate requiredRole="client">
            <Badges>
              <Badge variant={lab.difficulty}>{lab.difficulty}</Badge>
              <Badge variant="default">{lab.category}</Badge>
              <Badge variant="default">{lab.time}</Badge>
            </Badges>
            <Title>{lab.title}</Title>
            <Desc>{lab.description}</Desc>
          </SharedAuthGate>
        </Container>
      </Section>
    );
  }

  const checkedCount = checks.filter(Boolean).length;

  return (
    <Section>
      <Container>
        <BackLink onClick={onBack}>← BACK TO LABS</BackLink>

        <Badges>
          <Badge variant={lab.difficulty}>{lab.difficulty}</Badge>
          <Badge variant="default">{lab.category}</Badge>
          <Badge variant="default">{lab.time}</Badge>
        </Badges>
        <Title>{lab.title}</Title>
        <Desc>{lab.description}</Desc>

        {completed && (
          <div style={{ marginBottom: "24px" }}>
            <CompleteBadge>COMPLETED</CompleteBadge>
          </div>
        )}
        {saveError && <SaveError>{saveError}</SaveError>}

        <Instructions label="how to use this lab">
          <h4>What's here</h4>
          <p>
            A hands-on DevOps challenge. You're building the thing in your
            own environment — your shell, your AWS account, your container
            runtime. We give you the spec, the hints, and the resources.
          </p>
          <h4>Interactive shell</h4>
          <p>
            Hit <strong>LAUNCH INTERACTIVE SHELL</strong> below to open a
            browser terminal into a fresh, isolated sandbox pre-loaded with this
            lab's objectives + starter code and stocked with docker, k3d,
            kubectl, helm, terraform, and LocalStack. Nothing you run touches
            your machine — the sandbox is torn down when you close it. (Trainees
            and admins only.)
          </p>
          <h4>How to work through it</h4>
          <ol>
            <li>Read the description + prerequisites carefully.</li>
            <li>Launch the interactive shell — your workspace + objectives are staged under <code>~/labs/</code>.</li>
            <li>
              Work through the objectives one by one. Check each box as you
              complete it — progress saves automatically when signed in.
            </li>
            <li>
              Stuck? Open a hint. Hints are graded — first one is gentle,
              last one is the answer.
            </li>
            <li>
              Hit <strong>MARK AS COMPLETE</strong> when all objectives are
              checked. Earns 50 XP (+75 bonus on first-try pass).
            </li>
          </ol>
          <h4>Lives</h4>
          <p>
            5 lives shown above the action row. Future iterations of the
            verify flow will spend a life on each wrong attempt — for now
            the verifier accepts your self-check.
          </p>
        </Instructions>

        {/* Prerequisites */}
        <Card>
          <CardLabel>PREREQUISITES</CardLabel>
          <ul style={{ padding: 0, margin: 0 }}>
            {lab.prerequisites.map((p, i) => <PrereqItem key={i}>{p}</PrereqItem>)}
          </ul>
        </Card>

        {/* Objectives */}
        <Card>
          <CardLabel>OBJECTIVES ({checkedCount}/{lab.objectives.length})</CardLabel>
          {lab.objectives.map((obj, i) => (
            <ObjectiveRow key={i}>
              <Checkbox
                checked={checks[i]}
                onClick={() => toggleCheck(i)}
                aria-label={`${checks[i] ? "Uncheck" : "Check"}: ${obj}`}
              >
                {checks[i] ? "✓" : ""}
              </Checkbox>
              <ObjectiveText checked={checks[i]}>{obj}</ObjectiveText>
            </ObjectiveRow>
          ))}
          <ProgressLabel>{checkedCount} OF {lab.objectives.length} COMPLETE</ProgressLabel>
        </Card>

        {/* Hints */}
        <Card>
          <CardLabel>HINTS</CardLabel>
          {lab.hints.map((hint, i) => (
            <div key={i}>
              <HintHeader onClick={() => toggleHint(i)}>
                {hint.title}
                <HintArrow open={revealedHints.includes(i)}>▶</HintArrow>
              </HintHeader>
              <HintBody open={revealedHints.includes(i)}>{hint.body}</HintBody>
            </div>
          ))}
        </Card>

        {/* Resources */}
        <Card>
          <CardLabel>RESOURCES</CardLabel>
          {lab.resources.map((r, i) => (
            <ResourceLink key={i} href={r.url} target="_blank" rel="noopener noreferrer">
              {r.title} ↗
            </ResourceLink>
          ))}
        </Card>

        {/* Lives status */}
        <div style={{ marginBottom: 16 }}>
          <LivesIndicator />
        </div>

        {/* Interactive shell */}
        <Card>
          <CardLabel>INTERACTIVE SHELL</CardLabel>
          {(() => { const bannerText = labHealthBannerText(labHealth); return bannerText && <HealthBanner>{bannerText}</HealthBanner>; })()}
          {!shellOpen ? (
            <PrimaryBtn
              type="button"
              onClick={() => setShellOpen(true)}
              aria-label="Launch interactive shell"
              disabled={labHealth != null && labHealth.ok === false}
            >
              LAUNCH INTERACTIVE SHELL
            </PrimaryBtn>
          ) : (
            <>
              <LabTerminal labId={lab.id} />
              <div style={{ marginTop: 12 }}>
                <SecondaryBtn as="button" type="button" onClick={() => setShellOpen(false)} style={{ cursor: "pointer" }} aria-label="Close shell">
                  CLOSE SHELL
                </SecondaryBtn>
              </div>
            </>
          )}
        </Card>

        {/* Actions */}
        <ActionRow>
          {lab.starterRepo && (
            <SecondaryBtn href={lab.starterRepo} target="_blank" rel="noopener noreferrer" aria-label="Open starter repo on GitHub">
              STARTER REPO
            </SecondaryBtn>
          )}
          {!completed ? (
            <PrimaryBtn
              onClick={markComplete}
              disabled={checkedCount < lab.objectives.length}
              aria-label="Mark lab as complete"
            >
              {checkedCount < lab.objectives.length
                ? `COMPLETE ALL OBJECTIVES FIRST`
                : `MARK AS COMPLETE`}
            </PrimaryBtn>
          ) : (
            <SecondaryBtn
              as="button"
              onClick={undoComplete}
              aria-label="Undo completion"
              style={{ cursor: "pointer" }}
            >
              UNDO COMPLETION
            </SecondaryBtn>
          )}
        </ActionRow>
      </Container>
    </Section>
  );
};

export default LabDetail;
